import { useState } from 'react';

// ─── Design tokens ─────────────────────────────────────────────────────────────
const BASE_FILL = '#eef2f6';   // Neutral clean medical gray
const TRACE_LINE = '#94a3b8';  // Crisp outline color
const INJURY_FILL = 'rgba(239, 68, 68, 0.45)'; // Semi-transparent clinical red
const HOVER_FILL = 'rgba(148, 163, 184, 0.3)';

function mirrorPath(d, cx = 50) {
    if (!d) return '';
    return d.trim().split(/(?=[MZC])/).map(cmd => {
        cmd = cmd.trim(); if (!cmd) return ''; if (cmd === 'Z') return 'Z';
        const t = cmd[0]; const coords = cmd.slice(1).trim().split(/[\s,]+/).map(Number);
        if (t === 'M' || t === 'L') return `${t} ${cx + (cx - coords[0])},${coords[1]}`;
        if (t === 'C') return `C ${cx + (cx - coords[0])},${coords[1]} ${cx + (cx - coords[2])},${coords[3]} ${cx + (cx - coords[4])},${coords[5]}`;
        return cmd;
    }).join(' ');
}

// Left side anterior (X < 50) - precise bezier curves maintaining clinical proportions
const F_LEFT = {
    cabeza: "M 50,10 C 42,10 36,15 36,25 C 36,32 38,36 41,39 C 45,36 48,35 50,35 Z",
    cara: "M 41,39 C 43,44 46,47 50,47 C 49,43 47,40 50,35 C 48,35 45,36 41,39 Z",
    cuello: "M 44,46 C 44,52 42,54 38,55 C 46,57 48,56 50,56 C 48,54 46,49 50,47 C 46,47 44,46 44,46 Z",
    torax: "M 50,56 C 48,56 46,57 38,55 C 26,55 24,66 26,75 C 33,90 42,94 50,94 Z",
    abdomen: "M 50,94 C 42,94 33,90 26,75 C 25,95 28,110 32,120 C 40,123 46,124 50,124 Z",
    pelvis: "M 50,124 C 46,124 40,123 32,120 C 30,127 28,134 27,142 C 34,148 44,152 50,154 Z",
    hombro_izq: "M 38,55 C 32,54 26,58 22,64 C 18,70 19,76 21,80 C 25,75 28,70 26,75 C 24,66 26,55 38,55 Z",
    brazo_izq: "M 21,80 C 18,88 17,96 17,104 C 20,108 24,106 27,104 C 28,95 28,85 26,75 C 28,70 25,75 21,80 Z",
    antebrazo_izq: "M 17,104 C 14,116 13,126 15,136 C 18,138 22,136 24,134 C 26,124 28,114 27,104 C 24,106 20,108 17,104 Z",
    mano_izq: "M 15,136 C 13,144 12,152 14,158 C 17,162 20,160 22,156 C 24,148 25,140 24,134 C 22,136 18,138 15,136 Z",
    muslo_izq: "M 27,142 C 24,155 23,170 25,185 C 31,188 36,188 41,185 C 44,172 47,159 50,154 C 44,152 34,148 27,142 Z",
    rodilla_izq: "M 25,185 C 25,192 26,198 27,204 C 32,206 37,206 41,204 C 42,198 42,192 41,185 C 36,188 31,188 25,185 Z",
    pierna_izq: "M 27,204 C 26,220 27,235 31,250 C 34,252 38,251 40,248 C 41,235 42,220 41,204 C 37,206 32,206 27,204 Z",
    pie_izq: "M 31,250 C 28,256 26,262 26,268 C 32,272 38,272 42,268 C 42,262 41,256 40,248 C 38,251 34,252 31,250 Z"
};

// Posterior Left (Back view) - very similar bounds, different muscular lines
const B_LEFT = {
    craneo_d: "M 50,10 C 42,10 36,15 36,25 C 36,32 38,36 41,39 C 45,38 48,37 50,37 Z",
    nuca: "M 41,39 C 44,46 46,47 50,47 C 48,42 46,40 50,37 C 48,37 45,38 41,39 Z",
    espalda_alta: "M 50,47 C 46,47 44,46 41,39 C 39,44 38,50 38,55 C 26,55 24,66 26,75 C 33,85 42,90 50,90 Z",
    espalda_baja: "M 50,90 C 42,90 33,85 26,75 C 26,90 29,105 32,116 C 40,120 45,122 50,122 Z",
    lumbar: "M 50,122 C 45,122 40,120 32,116 C 30,125 28,134 27,142 C 34,140 44,142 50,146 Z",
    hombro_izq_d: F_LEFT.hombro_izq,
    brazo_izq_d: F_LEFT.brazo_izq,
    antebrazo_izq_d: F_LEFT.antebrazo_izq,
    mano_izq_d: F_LEFT.mano_izq,
    gluteo_izq: "M 27,142 C 24,155 23,170 25,185 C 31,188 36,188 41,185 C 44,172 47,159 50,146 C 44,142 34,140 27,142 Z", // Encompasses back thigh + glute
    pantorrilla_izq: "M 25,185 C 24,206 27,235 31,252 C 34,254 38,253 40,250 C 41,235 42,215 41,185 C 36,188 31,188 25,185 Z", // From knee bend down
    talon_izq: "M 31,252 C 29,258 28,264 28,270 C 33,272 37,272 41,270 C 41,264 40,258 40,250 C 38,253 34,254 31,252 Z"
};

// Generator helper
function buildZones(halfDict, isBack) {
    const list = [];
    Object.entries(halfDict).forEach(([k, d]) => {
        // Left (screen left is anatomical right for anterior? User requested Izq/Der IDs preserved perfectly)
        list.push({ id: k, d: d, isLeft: true });
        // Right
        if (k.includes('_izq')) {
            let nameRight = k.replace('_izq', '_der');
            list.push({ id: nameRight, d: mirrorPath(d), isLeft: false });
        } else {
            // Un-split central elements missing _left logic, we mirror the path and merge them
            const rightD = mirrorPath(d);
            list.push({ id: k + '_rightHalf', isRightHalfFor: k, d: rightD });
        }
    });

    // Merge center halves
    const finalZones = [];
    const grouped = {};
    list.forEach(item => {
        if (item.isRightHalfFor) {
            grouped[item.isRightHalfFor] = (grouped[item.isRightHalfFor] || '') + ' ' + item.d;
        } else {
            grouped[item.id] = (grouped[item.id] || '') + ' ' + item.d;
        }
    });

    // Convert to proper zone objects with labels
    Object.entries(grouped).forEach(([id, d]) => {
        finalZones.push({ id, d: d.trim(), label: formatLabel(id, isBack) });
    });
    return finalZones;
}

function formatLabel(id, isBack) {
    const s = id.replace(/_d$/, '').replace(/_b$/, '').replace(/_/g, ' ');
    return s.charAt(0).toUpperCase() + s.slice(1) + (isBack ? ' (Post.)' : '');
}

const FRONT_ZONES = buildZones(F_LEFT, false);
const BACK_ZONES = buildZones(B_LEFT, true);

// ─── Component ───────────────────────────────────────────────────────────────

function HitArea({ zone, selected, hovered, onClick, onEnter, onLeave, readOnly }) {
    const fill = selected ? INJURY_FILL : hovered ? HOVER_FILL : BASE_FILL;
    const stroke = selected ? '#dc2626' : hovered ? '#cbd5e1' : TRACE_LINE;

    return (
        <path
            d={zone.d}
            fill={fill}
            stroke={stroke}
            strokeWidth={0.6}
            strokeLinejoin="round"
            cursor={readOnly ? 'default' : 'pointer'}
            onClick={readOnly ? undefined : onClick}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
            style={{ transition: 'fill 0.15s, stroke 0.15s' }}
        />
    );
}

function BodyFigure({ zones, selected, onToggle, title, readOnly }) {
    const [hovered, setHovered] = useState(null);

    return (
        <div className="flex flex-col items-center gap-3">
            <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest select-none">
                {title}
            </h3>

            <div className="relative">
                <svg viewBox="0 0 100 280" width="130" height="364" className="drop-shadow-sm overflow-visible">
                    {zones.map(zone => (
                        <HitArea
                            key={zone.id}
                            zone={zone}
                            selected={selected.includes(zone.id)}
                            hovered={hovered === zone.id}
                            readOnly={readOnly}
                            onClick={() => onToggle(zone.id)}
                            onEnter={() => setHovered(zone.id)}
                            onLeave={() => setHovered(null)}
                        />
                    ))}
                </svg>

                {hovered && !readOnly && (
                    <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[11px] font-bold px-2 py-1 rounded shadow-lg whitespace-nowrap z-30" style={{ top: -24 }}>
                        {zones.find(z => z.id === hovered)?.label}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function BodyDiagram({ value = [], onChange, readOnly = false }) {
    const toggle = id => {
        if (readOnly) return;
        onChange(value.includes(id) ? value.filter(z => z !== id) : [...value, id]);
    };

    return (
        <div className="flex justify-center gap-12 sm:gap-24 flex-wrap pb-4">
            <BodyFigure
                zones={FRONT_ZONES}
                selected={value}
                onToggle={toggle}
                title="ANTERIOR"
                readOnly={readOnly}
            />
            <BodyFigure
                zones={BACK_ZONES}
                selected={value}
                onToggle={toggle}
                title="POSTERIOR"
                readOnly={readOnly}
            />
        </div>
    );
}
